import { z } from "zod";
/**
 * What a company sells, in its own words, read from its own site.
 *
 * The gap this closes: prompt research was told *nothing* about the company. The
 * brand seed sent a brand id, the brief said "the customer has not said what
 * they sell yet, so stay close to the category", and a model asked to write
 * buyer questions for an unknown company in an unknown category correctly wrote
 * the most generic commerce questions in existence. The output was not a model
 * failure. It was the only honest answer to that input.
 *
 * **This is a model output and it may never become a measurement** (§14a). It
 * shapes *which questions we ask*; it never touches what an engine said. The
 * type below is the enforcement: there is no field here for a mention, a
 * citation, a rank, a score or a competitor's standing, and there must never be
 * one. It describes a company. It reports nothing.
 *
 * **It is also not a claim about the company being true.** It is a summary of
 * what their own marketing pages say, which is a different thing and is the
 * right input for "what would a buyer type" — buyers read the same pages.
 */
/** Where the brief came from, kept because a summary is only as good as its source. */
export const briefSourceSchema = z.object({
    /**
     * How we came to know this, and the two are not equally good.
     *
     * `read` means we fetched the company's own pages and summarised them. That
     * is grounded: a buyer reads the same words.
     *
     * `recalled` means the site would not let us in — a bot block, a challenge
     * page — and the model was asked what it knows about that domain instead. It
     * is a weaker source and it is recorded as one, for the same reason a proxy
     * capture is never averaged with a truth capture: a reader who cannot tell
     * the two apart will treat them the same (§1, §14a).
     *
     * A recalled brief still never becomes a measurement. It shapes which
     * questions we ask, exactly as a read one does.
     */
    kind: z.enum(["read", "recalled"]),
    /** The pages actually read. Empty on a recalled brief, and that is the tell. */
    urls: z.array(z.string()),
    /**
     * What `robots.txt` permitted, in the words of the rule that decided it.
     *
     * Stored rather than checked and forgotten: §11 asks that we record what we
     * were permitted to do. "no_robots_file" is a real answer and not a gap.
     */
    robots: z.string(),
    fetchedAt: z.string(),
});
export const PRODUCT_RANGE_NAME_MAX = 80;
export const PRODUCT_RANGE_PRICE_BAND_MAX = 80;
export const PRODUCT_RANGE_DETAIL_MAX = 300;
/** One range or collection, as the company's own pages present it. */
export const productRangeSchema = z.object({
    /** The range or collection name, e.g. a line of watches rather than one SKU. */
    name: z.string().min(1).max(PRODUCT_RANGE_NAME_MAX),
    /** What it costs, in the site's own words. Empty when the pages do not say. */
    priceBand: z.string().max(PRODUCT_RANGE_PRICE_BAND_MAX),
    /** What it is and who it suits, plus any spec the pages state. */
    detail: z.string().max(PRODUCT_RANGE_DETAIL_MAX),
});
export const brandBriefSchema = z.object({
    brandId: z.string(),
    /**
     * What the company sells, in two sentences at most.
     *
     * The single most valuable field. "A same-day courier for online sellers,
     * handling cash on delivery" produces questions about couriers; the absence
     * of it produced questions about buying things online.
     */
    sells: z.string(),
    /** The category a buyer would search in, not the one an analyst would file it under. */
    category: z.string(),
    /** Who buys it. A question written for the wrong buyer measures the wrong answer. */
    buyers: z.string(),
    /**
     * The words this company and its buyers use for the thing being sold.
     *
     * Kept as a list because it is the raw material of a question: a buyer types
     * the words they know, and those are frequently not the words a category page
     * uses. Never brand names — a question naming a brand measures nothing, and
     * `checkPrompt` refuses it downstream anyway.
     */
    vocabulary: z.array(z.string()),
    /**
     * The ranges this company sells, as its own pages describe them.
     *
     * Added because a brand name on its own is not usable. A model asked to
     * recommend the brand while forbidden from inventing a price or a spec has
     * nothing it can honestly say, and says nothing: across fifteen test threads
     * a writer given only `sells` and `category` named the brand in at most one,
     * including in threads where another commenter had already named it. Given
     * ranges with price bands it named the brand in nine, every mention carrying
     * a real band and the tradeoff beside it.
     *
     * From the pages and nowhere else. `priceBand` and `detail` are empty when
     * the site does not say, which is a normal state rather than a gap to fill:
     * an invented price is the one error this whole path exists to prevent.
     */
    products: z.array(productRangeSchema).max(20).default([]),
    source: briefSourceSchema,
    /** Which model wrote it. A summary is only as good as its author (§14a.6). */
    model: z.string(),
});
/**
 * The brief, or the reason there is not one.
 *
 * Four outcomes and no fifth, because each needs a different sentence on screen
 * and a count of failures would hide all four:
 *
 * - `ready` — we read the site and summarised it.
 * - `refused_by_robots` — the site told us not to. We did not fetch, and the
 *   screen asks the customer to describe the business instead.
 * - `unreadable` — neither the site nor the model could tell us anything. Rare,
 *   and it is the only one that ends with a question on screen.
 * - `no_model` — no model is configured, so nothing can be summarised. Absent
 *   config is stated, never stubbed (§14a.4).
 */
export const briefOutcomeSchema = z.discriminatedUnion("outcome", [
    z.object({ outcome: z.literal("ready"), brief: brandBriefSchema }),
    z.object({ outcome: z.literal("refused_by_robots"), rule: z.string() }),
    z.object({ outcome: z.literal("unreadable") }),
    z.object({ outcome: z.literal("no_model") }),
]);
/**
 * Describing the business by hand, when the site cannot be read.
 *
 * The fallback for every outcome above that is not `ready`, and the field the
 * `brand` research seed always had and nothing ever filled. One sentence from a
 * person beats four pages we were not allowed to fetch.
 */
export const describeBrandSchema = z.object({
    describes: z.string().trim().min(10).max(400),
});
//# sourceMappingURL=brief.js.map